import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * If a ScrollTrigger somehow never fires - a layout quirk, a refresh race, a
 * browser that mis-measures - gsap.from() has already set opacity:0 and the
 * block would stay invisible. After this long, give up on animating and let
 * the content render normally. Failure mode becomes "no animation", never
 * "no content".
 */
const FAILSAFE_MS = 2500;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Scroll-triggered reveal.
 *
 * Wraps its children and animates them in as the block enters the viewport.
 * Pass `selector` to stagger the matching descendants instead of the wrapper
 * itself - e.g. <Reveal selector="[data-card]" stagger={0.08}>.
 *
 *   <Reveal>                   fade + rise, as one block
 *   <Reveal y={0} x={-40}>     slide in from the left
 *   <Reveal selector="li">     stagger the list items
 *
 * Nothing is hidden by CSS: gsap.from() applies its start values immediately,
 * inside useLayoutEffect and so before the first paint. If GSAP fails to load
 * or the visitor prefers reduced motion, the content simply renders as normal
 * rather than being stranded invisible.
 */
export default function Reveal({
  children,
  as: Tag = "div",
  className,
  selector,
  y = 36,
  x = 0,
  scale,
  duration = 0.85,
  delay = 0,
  stagger = 0.09,
  start = "top 85%",
  ...rest
}) {
  const scopeRef = useRef(null);

  useLayoutEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return undefined;
    if (prefersReducedMotion()) return undefined;

    const targets = selector
      ? Array.from(scope.querySelectorAll(selector))
      : [scope];
    if (targets.length === 0) return undefined;

    let tween;
    const ctx = gsap.context(() => {
      tween = gsap.from(targets, {
        opacity: 0,
        y,
        x,
        ...(scale === undefined ? {} : { scale }),
        duration,
        delay,
        stagger,
        ease: "power3.out",
        scrollTrigger: {
          trigger: scope,
          start,
          once: true,
        },
      });
    }, scope);

    const failsafe = window.setTimeout(() => {
      if (!tween || tween.isActive() || tween.progress() > 0) return;
      tween.scrollTrigger?.kill();
      tween.kill();
      targets.forEach((node) => {
        node.style.opacity = "";
        node.style.transform = "";
      });
    }, FAILSAFE_MS);

    return () => {
      window.clearTimeout(failsafe);
      ctx.revert();
    };
  }, [selector, y, x, scale, duration, delay, stagger, start]);

  return (
    <Tag ref={scopeRef} className={className} {...rest}>
      {children}
    </Tag>
  );
}
